import React, {
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useMyState } from "../utils";
import useCallBack, { Callbacks } from "../utils/callback/useCallBack";
import { getDocType } from "../utils/getDocType";

export interface UseEmailProps {
  mailSubject: string;
  mailTo: Array<string>;
  mailFrom: string;
  mailBody: string;
  mailBodyHTML: string;
  attachments: Array<any>
  mailId: string;
  emailLoading: boolean;
  isCompose: boolean
  getAttachmentContent: (attachmentId: string, callback?: Callbacks<any>) => void
}

export interface UseEmailLoadingProps {
  subject: boolean;
  email: boolean;
  from: boolean;
  body: boolean;
  attachments: boolean
  id: boolean;
}

interface MailContextProps extends UseEmailProps {
  refetch: () => void
}

// Create the context with a default value
const MailContext = createContext<MailContextProps | undefined>(undefined);

// Create a provider component
interface MailProviderProps {
  children: ReactNode;
}

export const MailProvider: React.FC<MailProviderProps> = ({ children }) => {
  const item = Office.context.mailbox.item
  const isCompose = Boolean(item?.body.setSelectedDataAsync)
  const [refetch, setRefetch] = React.useState(false)

  const getAttachmentContent = (attachmentId: string, callback?: Callbacks<any>) => {
    const { onSuccess } = useCallBack(callback)
    item?.getAttachmentContentAsync(
      attachmentId,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          onSuccess?.(result)
          // console.log("Attachment Content:", result.value);

          /*
            result.value.format:
            - Base64
            - Eml
            - ICalendar
            - Url
          */

          // console.log("Format:", result.value.format);
          // console.log("Content:", result.value.content);
        } else {
          console.error(result.error);
        }
      }
    );
  };
  const [emailData, setEmailData] = useMyState<UseEmailProps>({
    mailSubject: "",
    mailBody: "",
    mailBodyHTML: "",
    mailFrom: "",
    mailId: "",
    mailTo: [],
    attachments: [],
    emailLoading: false,
    isCompose,
    getAttachmentContent

  });

  const [loading, setLoading] = useMyState<UseEmailLoadingProps>({
    email: true,
    from: true,
    body: true,
    subject: true,
    attachments: true,
    id: true,
  });

  const handleRefetch = () => {
    setLoading('body', true)
    setLoading('email', true)
    setLoading('from', true)
    setLoading('subject', true)
    setRefetch(prev => !prev)
  }



  const getAttachments = () => {
    try {
      // Works in both compose and read mode
      const attachments =
        item?.attachments?.map((attachment: any) => ({
          id: attachment.id,
          name: attachment.name,
          size: attachment.size,
          contentType: attachment.contentType,
          attachmentType: attachment.attachmentType,
          isInline: attachment.isInline,
        })) || [];

      setEmailData("attachments", attachments);
      setLoading("attachments", false);
    } catch (err) {
      console.error("Failed to get attachments", err);
      setLoading("attachments", false);
    }
  };

  const getRecipients = () => {
    try {
      if (isCompose) {
        item?.to.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const list = result.value?.map((v: any) => v.emailAddress).filter(Boolean) || [];
            setEmailData("mailTo", list);
            setLoading("email", false);
          }
        });
      } else {
        const list = item?.to?.map((v: any) => v.emailAddress).filter(Boolean) || [];
        setEmailData("mailTo", list);
        setLoading("email", false);
      }

    } finally {
      setLoading("email", false);
    }
  };

  React.useEffect(() => {
    //getting email id
    try {
      if (isCompose) {
        item?.saveAsync((saveResult) => {
          if (saveResult.status === Office.AsyncResultStatus.Failed) {
            console.error("saveAsync failed:", saveResult.error);
          } else {
            const ewsId = saveResult.value;
            setEmailData("mailId", ewsId);
            setLoading("id", false);
          }
        });
      } else {
        const mailId = item?.itemId;

        setEmailData("mailId", mailId!);
        setLoading("id", false);
      }

    } finally {
      setLoading("id", false);
    }
  }, []);
  React.useEffect(() => {
    // getting mailTo
    getRecipients();
    // getting attachments

    getAttachments()
    // getting mail subject
    try {
      if (isCompose) {
        item?.subject.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            setEmailData("mailSubject", result?.value);
            setLoading("subject", false);
          }
        });
      } else {
        setEmailData("mailSubject", item?.subject || "");
        setLoading("subject", false);
      }

    } finally {
      setLoading("subject", false);
    }

    // getting mailFrom
    try {
      if (isCompose) {
        item?.from.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const from = result.value.emailAddress;
            setEmailData("mailFrom", from);
            setLoading("from", false);
          }
        });
      } else {
        const from = item?.from?.emailAddress || "";

        setEmailData("mailFrom", from);
        setLoading("from", false);
      }

    } finally {
      setLoading("from", false);
    }
    // getting mail Body
    try {
      item?.body.getAsync(
        Office.CoercionType.Text, // or Office.CoercionType.Text
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to get body: " + asyncResult.error.message);
          } else {
            const body = asyncResult.value;
            setEmailData("mailBody", body);
            setLoading("body", false);
          }
        }
      );
      item?.body.getAsync(
        Office.CoercionType.Html,
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            const body = asyncResult.value;
            setEmailData("mailBodyHTML", body);
            setLoading("body", false);
          }
        }
      );

    } finally {
      setLoading("body", false);
    }
    if (isCompose) {
      item?.addHandlerAsync(
        Office.EventType.RecipientsChanged,
        getRecipients,
        (asyncResult) => {
          if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            console.error("Failed to attach RecipientsChanged handler");
          }
        }
      );
    }

    // Cleanup handlers when component unmounts
    return () => {
      try {
        if (isCompose) {
          item?.removeHandlerAsync(Office.EventType.RecipientsChanged, {
            asyncContext: getRecipients,
          });
        }

      } catch (err) {
        console.warn("Failed to remove handlers", err);
      }
    };
  }, [refetch]);

  React.useEffect(() => {
    if (loading.email || loading.subject || loading.from || loading.body || loading.id) {
      setEmailData("emailLoading", true);
    }
    setEmailData("emailLoading", false);
  }, [loading.email, loading.subject, loading.from, loading.body, loading.id]);
  return (
    <>
      <MailContext.Provider
        value={{
          ...emailData,
          refetch: handleRefetch
        }}
      >
        {children}
      </MailContext.Provider>
    </>
  );
};

// Create a custom hook for consuming the context
export const useEmail = (): MailContextProps => {
  const context = useContext(MailContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
